import React, { useState } from "react";
import styled from "styled-components";

import { OutreachLead, OutreachRailSnapshot } from "../../../../types/OutreachMetrics";
import Modal, { ModalView } from "./Modal";
import { Trend } from "./RailCapacity";
import InstagramAccountHealth from "./InstagramAccountHealth";
import InstagramLeadSequences from "./InstagramLeadSequences";
import LinkedInLeadSequences from "./LinkedInLeadSequences";

// Everything that used to be a second equally weighted tab about the machine.
// It opens from the rail table, because that is the number it explains.

const VIEWS: ModalView[] = [
  { id: "throughput", label: "Throughput" },
  { id: "trend", label: "14 days" },
  { id: "channels", label: "Channels" },
];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const Message = styled.p`
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.55;
  color: var(--color-text-secondary);
`;

export type RailDetailProps = {
  accountId: string;
  snapshots: OutreachRailSnapshot[];
  leads: OutreachLead[];
  // The four worker lanes and their period breakdown, owned by the screen
  // because the modal they open lives there.
  throughput: React.ReactNode;
  // True while a lane modal or the lead drawer is open over this one.
  escapeDisabled?: boolean;
  onSelectLead: (lead: OutreachLead) => void;
  onClose: () => void;
};

const RailDetail: React.FC<RailDetailProps> = ({
  accountId,
  snapshots,
  leads,
  throughput,
  escapeDisabled,
  onSelectLead,
  onClose,
}) => {
  const [view, setView] = useState("throughput");

  return (
    <Modal
      title="Rail detail"
      subtitle="Throughput by period, the capacity trend, and the per channel workers."
      wide
      views={VIEWS}
      activeView={view}
      onView={setView}
      escapeDisabled={escapeDisabled}
      onClose={onClose}
    >
      {view === "throughput" && <Stack>{throughput}</Stack>}

      {view === "trend" && (
        <Stack>
          {snapshots.length < 2 ? (
            <Message>
              Two days of snapshots are needed before a trend means anything.
              There {snapshots.length === 1 ? "is" : "are"} {snapshots.length}.
            </Message>
          ) : (
            <Trend snapshots={snapshots} />
          )}
        </Stack>
      )}

      {view === "channels" && (
        <Stack>
          <InstagramAccountHealth accountId={accountId} />
          <InstagramLeadSequences accountId={accountId} />
          <LinkedInLeadSequences
            leads={leads}
            onSelectLead={(leadId) => {
              const lead = leads.find((l) => l.id === leadId);
              if (!lead) return;
              onClose();
              onSelectLead(lead);
            }}
          />
        </Stack>
      )}
    </Modal>
  );
};

export default RailDetail;
